import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, usePage } from '@inertiajs/react';
import { cn } from '../../lib/cn';
import ThemeToggle from '../ui/ThemeToggle';
import LanguageToggle from '../ui/LanguageToggle';
import ImpersonationBanner from './ImpersonationBanner';
import Sidebar, { getStudentNavItems } from './Sidebar';
import { useScrollTitle } from '../../hooks/useScrollTitle';
import { useUserNotifications } from '../../hooks/useUserNotifications';
import { truncateWords } from '../../lib/truncateWords';
import Logo from '../ui/Logo';
import InviteIsland from '../ui/InviteIsland';
import BreadcrumbBar from '../navigation/BreadcrumbBar';
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';
import { NavIcon, resolveNavIconKey } from './Sidebar';
import { getAvatarColorClasses } from '../../lib/avatarColors';

export default function AppShell({
  children,
  student,
  showHeader = true,
  maxWidth = 'default',
  className,
  scrollTitle: scrollTitleText,
}) {
  const { t } = useTranslation();
  const navItems = getStudentNavItems(t);
  const { scrollRef, visibleTitle } = useScrollTitle(scrollTitleText);
  const { invite, dismissInvite } = useUserNotifications(student?.id);

  const maxWidths = {
    narrow: 'max-w-3xl',
    default: 'max-w-7xl',
    wide: 'max-w-screen-2xl',
    full: 'max-w-full',
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      <ImpersonationBanner />
      <InviteIsland invite={invite} onDismiss={dismissInvite} />
      <div className="flex h-screen overflow-hidden">
        <Sidebar items={navItems} />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {showHeader && <StudentTopBar student={student} scrollTitle={visibleTitle} />}
          <main
            ref={scrollRef}
            className={cn(
              'flex-1 overflow-y-auto px-6 py-8',
              maxWidths[maxWidth],
              maxWidth !== 'full' && 'mx-auto w-full',
              className
            )}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}

// Vertical-nav axis URL patterns — see TeacherShell for the full explanation.
const VERTICAL_NAV_URL = /^\/(?:teacher|student)?\/?(?:library|dictionary|sentence_studio|morphemes)(?:\/|$)/;

function StudentTopBar({ student, scrollTitle }) {
  const { t } = useTranslation();
  const { crumbs } = useBreadcrumbs();
  const { url } = usePage();
  const onVerticalNav = VERTICAL_NAV_URL.test(url || '');
  const hasCrumbs = onVerticalNav && crumbs && crumbs.length > 0;
  const iconKey = resolveNavIconKey(url, student?.role);

  return (
    <header className="flex-shrink-0 h-14 bg-white/80 backdrop-blur-md border-b border-gray-200 dark:bg-gray-900/80 dark:border-gray-700 z-30">
      <div className="flex items-center justify-between h-full px-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Link href="/student/dashboard" className="flex items-center gap-2 flex-shrink-0">
            <Logo className="h-9" />
          </Link>

          {hasCrumbs ? (
            <div className="pl-3 border-l border-gray-400 dark:border-gray-400 min-w-0 flex items-center gap-2">
              {iconKey && (
                <NavIcon
                  iconKey={iconKey}
                  className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0"
                />
              )}
              <BreadcrumbBar />
            </div>
          ) : (
            /* Animated scroll title */
            <div
              className={cn(
                'overflow-hidden transition-all duration-300 ease-out min-w-0',
                scrollTitle ? 'max-w-md opacity-100' : 'max-w-0 opacity-0'
              )}
            >
              <span className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2 pl-3 border-l border-gray-400 dark:border-gray-400">
                {iconKey && (
                  <NavIcon
                    iconKey={iconKey}
                    className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0"
                  />
                )}
                <span className="block truncate">
                  {typeof scrollTitle === 'string' ? truncateWords(scrollTitle) : scrollTitle}
                </span>
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <ThemeToggle />
          {student ? (
            <UserMenu user={student} />
          ) : (
            <Link
              href="/login"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
            >
              {t('nav.logIn')}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

function UserMenu({ user }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initials = user.initials;
  const avatarColor = getAvatarColorClasses(user.avatar_color);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 focus:outline-none"
      >
        <span className="text-sm text-gray-600 dark:text-gray-400 hidden sm:inline">
          {user.first_name} {user.last_name}
        </span>
        <div
          className={cn(
            'w-8 h-8 rounded-full',
            avatarColor.gradientClass,
            'flex items-center justify-center',
            'text-xs font-medium',
            avatarColor.textClass,
            'shadow-[0_1px_4px_rgba(0,0,0,0.1)]',
            'hover:ring-2 hover:ring-gray-300 dark:hover:ring-gray-500 transition-all'
          )}
        >
          {initials || '?'}
        </div>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {user.first_name} {user.last_name}
            </p>
            {user.email && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
            )}
          </div>
          <Link
            href="/student/profile"
            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            onClick={() => setIsOpen(false)}
          >
            {t('nav.profile')}
          </Link>
          <div className="border-t border-gray-100 dark:border-gray-700">
            <LanguageToggle />
          </div>
          <Link
            href="/logout"
            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-t border-gray-100 dark:border-gray-700"
            onClick={() => setIsOpen(false)}
          >
            {t('nav.logOut')}
          </Link>
        </div>
      )}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  backLink,
  backLabel = 'Back',
  actions,
  className,
}) {
  return (
    <div className={cn('mb-8', className)}>
      {backLink && (
        <a
          href={backLink}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-4 transition-colors duration-200"
        >
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {backLabel}
        </a>
      )}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{title}</h1>
          {subtitle && (
            <p className="mt-2 text-gray-500 dark:text-gray-400">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex items-center gap-3">{actions}</div>
        )}
      </div>
    </div>
  );
}

export function Section({ title, description, action, children, className }) {
  return (
    <section className={cn('mb-10', className)}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4">
          <div>
            {title && (
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
            )}
            {description && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
            )}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}) {
  return (
    <div
      className={cn(
        'text-center py-12 px-6',
        'rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700',
        className
      )}
    >
      {icon && (
        <div className="text-4xl text-gray-300 dark:text-gray-600 mb-4">{icon}</div>
      )}
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">{title}</h3>
      {description && (
        <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-sm mx-auto">{description}</p>
      )}
      {action}
    </div>
  );
}
